import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { useMenu } from '../../hooks/useData';
import { Badge } from '../../components/shared/KPIComponents';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Calendar, UtensilsCrossed, Wine, Coffee, Loader2 } from 'lucide-react';

const SECTION_ICONS = {
  'Entrées': UtensilsCrossed,
  'Vins Blancs': Wine,
  'Vins Rosés': Wine,
  'Vins Rouges': Wine,
  'Boissons': Coffee,
};

const DEFAULT_MENU = {
  dates: "Menu de la semaine",
  entrees: [],
  plats: [],
  desserts: [],
  formules: [
    { nom: "E+P+D", prix: 21 },
    { nom: "E+P ou P+D", prix: 19 },
    { nom: "Plat seul", prix: 15.5 }
  ]
};

const ChefCarte = () => {
  const { menu, loading: menuLoading } = useMenu();
  const [activeTab, setActiveTab] = useState('menu');
  const [carteItems, setCarteItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCarte = async () => {
      try {
        const res = await api.get('/carte');
        setCarteItems(res.data);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchCarte();
  }, []);

  const displayMenu = menu || DEFAULT_MENU;

  // Group items by section
  const sections = {};
  carteItems.forEach(item => {
    const sec = item.section || 'Autres';
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(item);
  });

  const renderCarteSection = (title, items) => {
    const IconComp = SECTION_ICONS[title];
    return (
      <div className="mb-6" key={title}>
        <h3 className="font-title text-lg text-lemas-green mb-3 flex items-center gap-2">
          {IconComp && <IconComp size={18} />}
          {title}
        </h3>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex justify-between items-center p-3 rounded-lg bg-[rgb(var(--lm-surface))]/50 border border-[rgb(var(--lm-border))]">
              <div className="flex-1">
                <p className="font-medium text-sm text-[rgb(var(--lm-text))]">{item.nom}</p>
                {item.description && <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{item.description}</p>}
              </div>
              <span className="font-mono text-sm text-[rgb(var(--lm-green))]">{formatCurrency(item.prix_ttc || item.prix || 0)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="chef-carte">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
          <TabsTrigger value="menu" className="flex-1 data-[state=active]:bg-lemas-green data-[state=active]:text-lemas-bg">
            Menu Semaine
          </TabsTrigger>
          <TabsTrigger value="carte" className="flex-1 data-[state=active]:bg-lemas-green data-[state=active]:text-lemas-bg">
            Carte Complete
          </TabsTrigger>
        </TabsList>

        {/* MENU TAB */}
        <TabsContent value="menu" className="mt-6">
          <div className="kpi-card border-lemas-green/30">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="text-lemas-green" size={20} />
              <h3 className="font-title text-xl text-[rgb(var(--lm-text))]">Menu de la semaine</h3>
            </div>
            <p className="text-sm text-lemas-green mb-4">{displayMenu.dates}</p>

            <div className="space-y-6">
              {displayMenu.entrees?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Entrees</h4>
                  <div className="space-y-2">
                    {displayMenu.entrees.map((e, i) => (
                      <div key={i} className="p-3 bg-[rgb(var(--lm-bg))]/50 rounded-lg font-medium text-sm text-[rgb(var(--lm-text))]">{e}</div>
                    ))}
                  </div>
                </div>
              )}

              {displayMenu.plats?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Plats</h4>
                  <div className="space-y-2">
                    {displayMenu.plats.map((p, i) => (
                      <div key={i} className="p-3 bg-[rgb(var(--lm-bg))]/50 rounded-lg">
                        <p className="font-medium text-sm text-[rgb(var(--lm-text))]">{p.nom}</p>
                        {p.accompagnement && <p className="text-sm text-lemas-green">+ {p.accompagnement}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {displayMenu.desserts?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Desserts</h4>
                  <div className="space-y-2">
                    {displayMenu.desserts.map((d, i) => (
                      <div key={i} className="p-3 bg-[rgb(var(--lm-bg))]/50 rounded-lg font-medium text-sm text-[rgb(var(--lm-text))]">{d}</div>
                    ))}
                  </div>
                </div>
              )}

              {displayMenu.formules?.length > 0 && (
                <div className="pt-4 border-t border-[rgb(var(--lm-border))]">
                  <h4 className="text-xs uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-3">Prix des formules</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {displayMenu.formules.map((f, i) => (
                      <div key={i} className="text-center p-3 bg-lemas-green/10 rounded-lg">
                        <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{f.nom}</p>
                        <p className="font-mono text-xl text-lemas-green">{formatCurrency(f.prix)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!displayMenu.entrees?.length && !displayMenu.plats?.length) && (
                <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-4">Aucun menu defini pour cette semaine</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* CARTE TAB */}
        <TabsContent value="carte" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-lemas-green" size={24} />
            </div>
          ) : Object.keys(sections).length > 0 ? (
            <>
              <p className="text-sm text-[rgb(var(--lm-text-secondary))] mb-4">
                Carte geree par le patron — {carteItems.length} plats
              </p>
              {Object.entries(sections).map(([sec, items]) => renderCarteSection(sec, items))}
            </>
          ) : (
            <p className="text-sm text-[rgb(var(--lm-text-muted))] text-center py-8">
              Aucun plat dans la carte. Le patron doit ajouter les plats depuis son interface.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChefCarte;
